/**
 * AWS WAF interception.
 *
 * When a WAF rule answers instead of our backend (CAPTCHA -> 405, Challenge ->
 * 202), it marks the response with `x-amzn-waf-action`. An Office add-in cannot
 * solve a CAPTCHA - the taskpane's API calls, the log shippers and the custom-
 * functions runtime are all background requests - so the add-in's only job is to
 * fail clearly and stay quiet:
 *
 *   - Detection is header-only. The body (which embeds the WAF challenge key and
 *     context) is never read, logged, or shown, and the aws-waf-token cookie is
 *     never touched. Nothing here tries to satisfy or bypass the check.
 *   - Callers do not retry a blocked request, and the log shippers back off:
 *     hammering a rate-based rule is what keeps an IP blocked.
 *   - The user sees one generic message with no internal detail.
 *
 * No imports, so httpClient, logger and actionLog can all use it without
 * creating an import cycle.
 */

/** Shown to the user. Generic on purpose: no rule names, IPs or internals. */
export const WAF_BLOCKED_MESSAGE =
  'InSync could not reach the server because a network security check blocked the request. ' +
  'Please wait a few minutes and try again. If this keeps happening, contact Support@insyncanalytics.com.';

/** How long the log shippers stay quiet after a blocked upload. */
export const WAF_BACKOFF_MS = 5 * 60 * 1000;

/** The WAF action that answered this response ('captcha', 'challenge', ...), or null. */
export function wafAction(response: { headers?: { get(name: string): string | null } } | null | undefined): string | null {
  try {
    const v = response?.headers?.get('x-amzn-waf-action');
    // Lower-cased and length-capped: it goes into logs, so never echo arbitrary input.
    return v ? String(v).trim().toLowerCase().slice(0, 32) : null;
  } catch {
    return null;
  }
}

export function isWafBlocked(response: { headers?: { get(name: string): string | null } } | null | undefined): boolean {
  return wafAction(response) !== null;
}
